import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexGrid,
    ApexLegend,
    ApexPlotOptions,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';

import { GenericComponent } from '../generic/generic.component';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { CC_CONSTANTS } from '../../assets/constants';
import {
    colorAec1BlueLight,
    colorAec3Green,
    colorAec4Orange,
    colorAec8RedDark,
    colorBlack,
} from 'src/app/shared/all.constants';
import {
    EDF_CUSTOMERS_TRV_CONSUMPTIONS_CHART,
    EDF_CUSTOMERS_TRV_COUNT_CHART,
    EDF_CUSTOMERS_TRV_INCOME_CHART,
    EDF_CUSTOMERS_TRV_KPIS,
    EDF_CUSTOMERS_TRV_MEAN_CONSUMPTIONS_CHART,
    EDF_CUSTOMERS_TRV_TARIFF_CHART,
} from '../../assets/keys';
import { convertNumber } from 'src/app/shared/all.helpers';

const KEYS = [
    EDF_CUSTOMERS_TRV_KPIS,
    EDF_CUSTOMERS_TRV_COUNT_CHART,
    EDF_CUSTOMERS_TRV_CONSUMPTIONS_CHART,
    EDF_CUSTOMERS_TRV_MEAN_CONSUMPTIONS_CHART,
    EDF_CUSTOMERS_TRV_INCOME_CHART,
    EDF_CUSTOMERS_TRV_TARIFF_CHART,
];

interface TrvKpiData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    userCount?: number;
    userCountEvolution?: number;

    consumption?: number;
    consumptionEvolution?: number;

    totalIncome?: number;
    totalIncomeEvolution?: number;

    userRate?: number;
    userRateEvolution?: number;
}

interface TrvUsersCountData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: number;
    total?: number;
    tb?: number;
    tj?: number;
    tv?: number;
    rate?: number;
}

interface TrvConsumptionsData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: number;
    tb?: number;
    tj?: number;
    tv?: number;
    growthRate?: number;
}

interface TrvMeanConsumptionsData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: number;
    tb?: number;
    tj?: number;
    tv?: number;
}

interface TrvIncomeData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: number;
    tb?: number;
    tj?: number;
    tv?: number;
    total?: number;
    growthRate?: number;
}

interface TrvTariffData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: number;
    tb?: number;
    tj?: number;
    tv?: number;
}

@UntilDestroy()
@Component({
    selector: 'app-edf-trv',
    templateUrl: './edf-trv.component.html',
    styleUrls: ['./edf-trv.component.scss'],
})
export class EdfTrvComponent extends GenericComponent {
    public texts = CC_CONSTANTS.trvUsers.texts;

    public kpis: Signal<TrvKpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_TRV_KPIS),
    ) as Signal<TrvKpiData[]>;

    public usersCountChartData: Signal<TrvUsersCountData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_TRV_COUNT_CHART),
    ) as Signal<TrvUsersCountData[]>;
    public usersCountChartError: boolean = true;
    public usersCountChartSeries: ApexAxisChartSeries = [];

    public usersCountDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({
            0: this.texts.usersCountChart.series[0].precision,
            1: this.texts.usersCountChart.series[1].precision,
            2: this.texts.usersCountChart.series[2].precision,
            3: this.texts.usersCountChart.series[3].precision,
            4: this.texts.usersCountChart.series[4].precision,
        }),
        style: this.setDataLabelsStyleColors(this.texts.usersCountChart.series[3].name),
    };
    public usersCountXaxisCategories: ApexXAxis['categories'];
    public usersCountYaxis: ApexYAxis[] = [];
    public usersCountCustomTooltip: ApexTooltip['custom'];
    public usersCountLegend: ApexLegend = {
        markers: {
            fillColors: [
                colorAec1BlueLight,
                colorAec4Orange,
                colorAec3Green,
                colorBlack,
                colorAec8RedDark,
            ],
        },
    };

    public consumptionsChartData: Signal<TrvConsumptionsData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_TRV_CONSUMPTIONS_CHART),
    ) as Signal<TrvConsumptionsData[]>;
    public consumptionsChartError: boolean = true;
    public consumptionsChartSeries: ApexAxisChartSeries = [];

    public consumptionsDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({
            0: this.texts.consumptionsChart.series[0].precision,
            1: this.texts.consumptionsChart.series[1].precision,
            2: this.texts.consumptionsChart.series[2].precision,
            3: this.texts.consumptionsChart.series[3].precision,
            4: this.texts.consumptionsChart.series[4].precision,
        }),
        style: this.setDataLabelsStyleColors(this.texts.consumptionsChart.series[3].name),
    };
    public consumptionsXaxisCategories: ApexXAxis['categories'];
    public consumptionsYaxis: ApexYAxis[] = [];
    public consumptionsCustomTooltip: ApexTooltip['custom'];
    public consumptionsLegend: ApexLegend = {
        markers: {
            fillColors: [
                colorAec1BlueLight,
                colorAec4Orange,
                colorAec3Green,
                colorBlack,
                colorAec8RedDark,
            ],
        },
    };

    public meanConsumptionsChartData: Signal<TrvMeanConsumptionsData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_TRV_MEAN_CONSUMPTIONS_CHART),
    ) as Signal<TrvMeanConsumptionsData[]>;
    public meanConsumptionsChartError: boolean = true;
    public meanConsumptionsChartSeries: ApexAxisChartSeries = [];
    public meanConsumptionsDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
    };
    public meanConsumptionsYaxis: ApexYAxis[] = [
        {
            seriesName: [
                this.texts.meanConsumptionsChart.series[0].name,
                this.texts.meanConsumptionsChart.series[1].name,
                this.texts.meanConsumptionsChart.series[2].name,
            ],
            min: 0,
            title: {
                text: this.texts.meanConsumptionsChart.yaxisTitleLeft,
            },
        },
    ];

    public incomeChartData: Signal<TrvIncomeData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_TRV_INCOME_CHART),
    ) as Signal<TrvIncomeData[]>;
    public incomeChartError: boolean = true;
    public incomeChartSeries: ApexAxisChartSeries = [];
    public incomeDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        style: this.setDataLabelsStyleColors(this.texts.incomeChart.series[3].name),
    };
    public incomeXaxisCategories: ApexXAxis['categories'];
    public incomeYaxis: ApexYAxis[] = [];
    public incomeLegend: ApexLegend = {
        markers: {
            fillColors: [
                colorAec1BlueLight,
                colorAec4Orange,
                colorAec3Green,
                colorBlack,
                colorAec8RedDark,
            ],
        },
    };

    public incomeCustomTooltip: ApexTooltip['custom'];

    public tariffChartData: Signal<TrvTariffData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_TRV_TARIFF_CHART),
    ) as Signal<TrvTariffData[]>;
    public tariffChartError: boolean = true;
    public tariffChartSeries: ApexAxisChartSeries = [];
    public tariffDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
    };
    public tariffYaxis: ApexYAxis[] = [
        {
            seriesName: [
                this.texts.tariffChart.series[0].name,
                this.texts.tariffChart.series[1].name,
                this.texts.tariffChart.series[2].name,
            ],
            min: 0,
            title: {
                text: this.texts.tariffChart.yaxisTitleLeft,
            },
        },
    ];
    public tariffGrid: ApexGrid = { padding: { left: 30 } };

    constructor(
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
        private readonly store: Store,
    ) {
        super(route, sidenavService, store);

        effect(() => {
            const usersCountChartData = this.usersCountChartData();
            this.usersCountChartError = this.checkForError(usersCountChartData, [
                'xYear',
                'tb',
                'tj',
                'tv',
                'rate',
            ]);
            if (!this.usersCountChartError) {
                this.usersCountXaxisCategories = usersCountChartData.map((data) => data.xYear);
                const totales = this.getTotales(usersCountChartData, ['tb', 'tj', 'tv']);

                const totalesMax = totales.reduce((max, t) => Math.max(max, t), 0);
                this.usersCountYaxis = [
                    {
                        seriesName: [
                            this.texts.usersCountChart.series[0].name,
                            this.texts.usersCountChart.series[1].name,
                            this.texts.usersCountChart.series[2].name,
                            this.texts.usersCountChart.series[3].name,
                        ],
                        min: 0,
                        max: Math.round(totalesMax / 100 + 1) * 100,
                        forceNiceScale: true,
                        tickAmount: 4,
                        title: {
                            text: this.texts.usersCountChart.yaxisTitleLeft,
                        },
                    },
                    {
                        seriesName: [this.texts.usersCountChart.series[4].name],
                        min: 0,
                        tickAmount: 4,
                        max: this.texts.userCount.maxRate,
                        opposite: true,
                        forceNiceScale: true,
                        title: {
                            text: this.texts.usersCountChart.yaxisTitleRight,
                        },
                    },
                ];
                this.usersCountChartSeries = [
                    {
                        name: this.texts.usersCountChart.series[0].name,
                        type: 'column',
                        data: usersCountChartData.map((data) => data.tb!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.usersCountChart.series[1].name,
                        type: 'column',
                        data: usersCountChartData.map((data) => data.tj!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.usersCountChart.series[2].name,
                        type: 'column',
                        data: usersCountChartData.map((data) => data.tv!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.usersCountChart.series[3].name,
                        type: 'line',
                        data: totales,
                        color: 'transparent',
                    },
                    {
                        name: this.texts.usersCountChart.series[4].name,
                        type: 'line',
                        data: usersCountChartData.map((data) => data.rate!),
                        color: colorAec8RedDark,
                    },
                ];
                this.usersCountDataLabels.enabledOnSeries = [0, 1, 2, 3, 4];
            }

            const consumptionsChartData = this.consumptionsChartData();
            this.consumptionsChartError = this.checkForError(consumptionsChartData, [
                'xYear',
                'tb',
                'tj',
                'tv',
                'growthRate',
            ]);
            if (!this.consumptionsChartError) {
                this.consumptionsXaxisCategories = consumptionsChartData.map((data) => data.xYear);
                const totales = this.getTotales(consumptionsChartData, ['tb', 'tj', 'tv']);
                const totalesMax = totales.reduce((max, t) => Math.max(max, t), 0);
                const rateMax = consumptionsChartData.reduce(
                    (max, data) => Math.max(max, data.growthRate ?? 0),
                    -Infinity,
                );
                const rateMin = consumptionsChartData.reduce(
                    (min, data) => Math.min(min, data.growthRate ?? 0),
                    Infinity,
                );
                this.consumptionsYaxis = [
                    {
                        seriesName: [
                            this.texts.consumptionsChart.series[0].name,
                            this.texts.consumptionsChart.series[1].name,
                            this.texts.consumptionsChart.series[2].name,
                            this.texts.consumptionsChart.series[3].name,
                        ],
                        min: 0,
                        max: Math.round(totalesMax / 100 + 1) * 100,
                        forceNiceScale: true,
                        tickAmount: 4,
                        title: {
                            text: this.texts.consumptionsChart.yaxisTitleLeft,
                        },
                    },
                    {
                        seriesName: [this.texts.consumptionsChart.series[4].name],
                        opposite: true,
                        min: Math.ceil(rateMin),
                        max: Math.round(rateMax) + 1,
                        forceNiceScale: true,
                        tickAmount: 4,
                        title: {
                            text: this.texts.consumptionsChart.yaxisTitleRight,
                        },
                    },
                ];
                this.consumptionsChartSeries = [
                    {
                        name: this.texts.consumptionsChart.series[0].name,
                        type: 'column',
                        data: consumptionsChartData.map((data) => data.tb!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.consumptionsChart.series[1].name,
                        type: 'column',
                        data: consumptionsChartData.map((data) => data.tj!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.consumptionsChart.series[2].name,
                        type: 'column',
                        data: consumptionsChartData.map((data) => data.tv!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.consumptionsChart.series[3].name,
                        type: 'line',
                        data: totales,
                        color: 'transparent',
                    },
                    {
                        name: this.texts.consumptionsChart.series[4].name,
                        type: 'line',
                        data: consumptionsChartData.map((data) => data.growthRate!),
                        color: colorAec8RedDark,
                    },
                ];

                this.consumptionsDataLabels.enabledOnSeries = [0, 1, 2, 3, 4];
            }

            const meanConsumptionsChartData = this.meanConsumptionsChartData();
            this.meanConsumptionsChartError = this.checkForError(meanConsumptionsChartData, [
                'xYear',
                'tb',
                'tj',
                'tv',
            ]);
            if (!this.meanConsumptionsChartError) {
                this.meanConsumptionsChartSeries = [
                    {
                        name: this.texts.meanConsumptionsChart.series[0].name,
                        type: 'line',
                        data: meanConsumptionsChartData.map((data) => ({
                            x: data.xYear!,
                            y: data.tb!,
                        })),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.meanConsumptionsChart.series[1].name,
                        type: 'line',
                        data: meanConsumptionsChartData.map((data) => ({
                            x: data.xYear!,
                            y: data.tj!,
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.meanConsumptionsChart.series[2].name,
                        type: 'line',
                        data: meanConsumptionsChartData.map((data) => ({
                            x: data.xYear!,
                            y: data.tv!,
                        })),
                        color: colorAec3Green,
                    },
                ];

                this.meanConsumptionsDataLabels.enabledOnSeries = [0, 1, 2];
            }

            const incomeChartData = this.incomeChartData();
            this.incomeChartError = this.checkForError(incomeChartData, [
                'xYear',
                'tb',
                'tj',
                'tv',
                'growthRate',
            ]);
            if (!this.incomeChartError) {
                this.incomeXaxisCategories = incomeChartData.map((data) => data.xYear);
                const totales = this.getTotales(incomeChartData, ['tb', 'tj', 'tv']);
                const totalesMax = totales.reduce((max, t) => Math.max(max, t), -Infinity);

                const rateMin = incomeChartData.reduce(
                    (min, t) => Math.min(min, t.growthRate ?? 0),
                    Infinity,
                );
                const rateMax = incomeChartData.reduce(
                    (max, t) => Math.max(max, t.growthRate ?? 0),
                    -Infinity,
                );

                this.incomeYaxis = [
                    {
                        seriesName: [
                            this.texts.incomeChart.series[0].name,
                            this.texts.incomeChart.series[1].name,
                            this.texts.incomeChart.series[2].name,
                            this.texts.incomeChart.series[3].name,
                        ],
                        min: 0,
                        max: Math.round(totalesMax / 40 + 1) * 40,
                        forceNiceScale: true,
                        tickAmount: 4,
                        decimalsInFloat: 0,
                        title: {
                            text: this.texts.incomeChart.yaxisTitleLeft,
                        },
                    },
                    // waiting for aec to give us the data for this series
                    {
                        seriesName: [this.texts.incomeChart.series[4].name],
                        min: Math.floor(rateMin) - (Math.floor(rateMin) % 2),
                        max: Math.ceil(rateMax) + (Math.ceil(rateMax) % 2),
                        forceNiceScale: true,
                        tickAmount: 4,
                        opposite: true,
                    },
                ];
                this.incomeChartSeries = [
                    {
                        name: this.texts.incomeChart.series[0].name,
                        type: 'column',
                        data: incomeChartData.map((data) => data.tb!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.incomeChart.series[1].name,
                        type: 'column',
                        data: incomeChartData.map((data) => data.tj!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.incomeChart.series[2].name,
                        type: 'column',
                        data: incomeChartData.map((data) => data.tv!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.consumptionsChart.series[3].name,
                        type: 'line',
                        data: totales,
                        color: 'transparent',
                    },
                    {
                        name: this.texts.incomeChart.series[4].name,
                        type: 'line',
                        data: incomeChartData.map((data) => data.growthRate!),
                        color: colorAec8RedDark,
                    },
                ];
                this.incomeDataLabels.enabledOnSeries = [0, 1, 2, 3, 4];
            }

            const tariffChartData = this.tariffChartData();
            this.tariffChartError = this.checkForError(tariffChartData, [
                'xYear',
                'tb',
                'tj',
                'tv',
            ]);
            if (!this.tariffChartError) {
                this.tariffChartSeries = [
                    {
                        name: this.texts.tariffChart.series[0].name,
                        type: 'line',
                        data: tariffChartData.map((data) => ({
                            x: data.xYear!,
                            y: data.tb!,
                        })),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.tariffChart.series[1].name,
                        type: 'line',
                        data: tariffChartData.map((data) => ({
                            x: data.xYear!,
                            y: data.tj!,
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.tariffChart.series[2].name,
                        type: 'line',
                        data: tariffChartData.map((data) => ({
                            x: data.xYear!,
                            y: data.tv!,
                        })),
                        color: colorAec3Green,
                    },
                ];
                this.tariffDataLabels.enabledOnSeries = [0, 1, 2];
            }
        });
    }

    ngOnInit() {
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.usersCountCustomTooltip = this.setUsersCountCustomTooltip();
        this.consumptionsCustomTooltip = this.setConsumptionsCustomTooltip();
        this.incomeCustomTooltip = this.setChartCustomTooltip(
            this.texts.incomeChart.precision,
            this.texts.incomeChart.units,
        );
    }

    private setUsersCountCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const bluePrecision = this.texts.usersCountChart.series[0].precision;
            const blueSerie = w.globals.initialSeries[0];
            const {
                name: blueName,
                color: blueColor,
                valueStringified: blueStringified,
            } = this.setCustomTooltipData(bluePrecision, blueSerie, dataPointIndex);

            const greenPrecision = this.texts.usersCountChart.series[1].precision;
            const greenSerie = w.globals.initialSeries[1];
            const {
                name: greenName,
                color: greenColor,
                valueStringified: greenStringified,
            } = this.setCustomTooltipData(greenPrecision, greenSerie, dataPointIndex);

            const yellowPrecision = this.texts.usersCountChart.series[2].precision;
            const yellowSerie = w.globals.initialSeries[2];
            const {
                name: yellowName,
                color: yellowColor,
                valueStringified: yellowStringified,
            } = this.setCustomTooltipData(yellowPrecision, yellowSerie, dataPointIndex);

            const totalPrecision = this.texts.usersCountChart.series[3].precision;
            const totalesSerie = w.globals.initialSeries[3];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(totalPrecision, totalesSerie, dataPointIndex);

            const ratePrecision = this.texts.usersCountChart.series[4].precision;
            const rateSerie = w.globals.initialSeries[4];
            const {
                name: rateName,
                color: rateColor,
                valueStringified: rateStringified,
            } = this.setCustomTooltipData(ratePrecision, rateSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: blueColor,
                    name: blueName,
                    value: blueStringified,
                },
                {
                    color: greenColor,
                    name: greenName,
                    value: greenStringified,
                },
                {
                    color: yellowColor,
                    name: yellowName,
                    value: yellowStringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
                {
                    color: rateColor,
                    name: rateName,
                    value: rateStringified + ' %',
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    private setConsumptionsCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const bluePrecision = this.texts.consumptionsChart.series[0].precision;
            const blueSerie = w.globals.initialSeries[0];
            const {
                name: blueName,
                color: blueColor,
                valueStringified: blueStringified,
            } = this.setCustomTooltipData(bluePrecision, blueSerie, dataPointIndex);

            const greenPrecision = this.texts.consumptionsChart.series[1].precision;
            const greenSerie = w.globals.initialSeries[1];
            const {
                name: greenName,
                color: greenColor,
                valueStringified: greenStringified,
            } = this.setCustomTooltipData(greenPrecision, greenSerie, dataPointIndex);

            const yellowPrecision = this.texts.consumptionsChart.series[2].precision;
            const yellowSerie = w.globals.initialSeries[2];
            const {
                name: yellowName,
                color: yellowColor,
                valueStringified: yellowStringified,
            } = this.setCustomTooltipData(yellowPrecision, yellowSerie, dataPointIndex);

            const totalPrecision = this.texts.consumptionsChart.series[3].precision;
            const totalesSerie = w.globals.initialSeries[3];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(totalPrecision, totalesSerie, dataPointIndex);

            const ratePrecision = this.texts.consumptionsChart.series[4].precision;
            const rateSerie = w.globals.initialSeries[4];
            const {
                name: rateName,
                color: rateColor,
                valueStringified: rateStringified,
            } = this.setCustomTooltipData(ratePrecision, rateSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: blueColor,
                    name: blueName,
                    value: blueStringified + ' GWh',
                },
                {
                    color: greenColor,
                    name: greenName,
                    value: greenStringified + ' GWh',
                },
                {
                    color: yellowColor,
                    name: yellowName,
                    value: yellowStringified + ' GWh',
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified + ' GWh',
                },
                {
                    color: rateColor,
                    name: rateName,
                    value: rateStringified + ' %',
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    public setChartCustomTooltip(
        precision: number | null = null,
        units: string[] = [],
    ): ApexTooltip['custom'] {
        return function ({ series, seriesIndex, dataPointIndex, w }) {
            const year = w.globals.categoryLabels[dataPointIndex];
            const hiddenSeries = new Set(w.globals.collapsedSeriesIndices);

            let tooltipHtml = `<div class="apexcharts-tooltip-title">${year}</div>`;

            (
                w.globals.initialSeries as Array<{ name: string; color?: string; data: any[] }>
            ).forEach((serie, idx) => {
                if (hiddenSeries.has(idx)) return;

                const serieName = serie.name;
                const serieColor = serieName === 'Total' ? colorBlack : serie.color || colorBlack;
                const dataPoint = serie.data[dataPointIndex];

                if (dataPoint !== undefined) {
                    const value =
                        precision !== null ? Number(dataPoint).toFixed(precision) : dataPoint;
                    const unit = units[idx] || '';

                    tooltipHtml += `
                        <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: ${idx}; display: flex; align-items: center;">
                            <span class="apexcharts-tooltip-marker" style="background-color: ${serieColor}; margin-right: 8px;"></span>
                            <div class="apexcharts-tooltip-text">
                                <div class="apexcharts-tooltip-y-group">
                                    <span class="apexcharts-tooltip-text-y-label">${serieName}:</span>
                                    <span class="apexcharts-tooltip-text-y-value">${convertNumber(value, precision, false)} ${unit}</span>
                                </div>
                            </div>
                        </div>
                    `;
                }
            });

            return tooltipHtml;
        };
    }
}
